import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_PAGE_SIZE,
  fetchTicketChatMessages,
} from "../../api/ticketChatApi";
import { createTicketChatSocket } from "../../api/ticketChatSocket";
import MessageList from "./MessageList";
import MessageComposer from "./MessageComposer";
import "./ticketChat.css";

const MAX_MESSAGE_LENGTH = 2000;

const toTimeValue = (value) => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const normalizeMessage = (payload) => {
  const message = String(payload?.message || "").trim();

  return {
    id: payload?.id ?? payload?._id ?? null,
    clientTempId: payload?.clientTempId,
    message,
    messageType: payload?.messageType || "text",
    createdAt: payload?.createdAt || new Date().toISOString(),
    senderName:
      payload?.senderName ||
      payload?.sender?.name ||
      payload?.userName ||
      payload?.name ||
      "Unknown",
    senderRole:
      payload?.senderRole ||
      payload?.sender?.role ||
      payload?.role ||
      "user",
    senderId: payload?.senderId ?? payload?.sender?.id ?? payload?.createdBy ?? null,
    isOwn: Boolean(payload?.isOwn),
    pending: Boolean(payload?.pending),
  };
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const ownRoleSet = new Set(["admin", "agent", "support", "deskala", "superadmin"]);
const incomingRoleSet = new Set(["user", "customer", "client"]);
const ownSenderNameSet = new Set(["super@deskala"]);
const inboundSenderNameSet = new Set(["deskala"]);

const isOwnMessage = (message, currentUser) => {
  const senderName = normalizeText(message?.senderName);
  if (senderName && inboundSenderNameSet.has(senderName)) return false;
  if (senderName && ownSenderNameSet.has(senderName)) return true;

  if (typeof message?.isOwn === "boolean") return message.isOwn;

  const messageSenderId = message?.senderId;
  const currentUserId = currentUser?.id ?? currentUser?.userId ?? null;
  if (messageSenderId !== null && currentUserId !== null) {
    if (String(messageSenderId) === String(currentUserId)) return true;
  }

  const userName = normalizeText(currentUser?.name || currentUser?.username);
  const userEmail = normalizeText(currentUser?.email);
  if (senderName && (senderName === userName || senderName === userEmail)) return true;

  const senderRole = normalizeText(message?.senderRole);
  if (senderRole && ownRoleSet.has(senderRole)) return true;
  if (senderRole && incomingRoleSet.has(senderRole)) return false;

  return false;
};

const sortMessages = (messages) =>
  [...messages].sort((a, b) => {
    const timeDiff = toTimeValue(a.createdAt) - toTimeValue(b.createdAt);
    if (timeDiff !== 0) return timeDiff;
    const aKey = String(a.id || a.clientTempId || "");
    const bKey = String(b.id || b.clientTempId || "");
    return aKey.localeCompare(bKey);
  });

const mergeDedupedMessages = (existing, incoming) => {
  const byId = new Map();
  const withoutId = [];

  [...existing, ...incoming].forEach((message) => {
    if (message.id !== null && message.id !== undefined) {
      const key = String(message.id);
      const prev = byId.get(key);
      byId.set(
        key,
        prev
          ? {
              ...prev,
              ...message,
              pending: false,
            }
          : message
      );
      return;
    }

    if (
      !withoutId.some(
        (item) =>
          item.clientTempId &&
          message.clientTempId &&
          String(item.clientTempId) === String(message.clientTempId)
      )
    ) {
      withoutId.push(message);
    }
  });

  return sortMessages([...Array.from(byId.values()), ...withoutId]);
};

const connectionLabelByState = {
  connected: "Connected",
  connecting: "Connecting",
  disconnected: "Disconnected",
  reconnecting: "Reconnecting",
};

export const TicketChatPanel = ({
  ticketId,
  companyId,
  token,
  currentUser,
  hideHeader = false,
  className = "",
}) => {
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [socketError, setSocketError] = useState("");
  const [connectionState, setConnectionState] = useState("disconnected");
  const [nextPage, setNextPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const socketRef = useRef(null);

  const normalizedTicketId = Number(ticketId);
  const normalizedCompanyId = Number(companyId);

  const roomPayload = useMemo(
    () => ({
      companyId: normalizedCompanyId,
      supportTicketId: normalizedTicketId,
    }),
    [normalizedCompanyId, normalizedTicketId]
  );

  const isContextValid = Boolean(
    token &&
      Number.isFinite(normalizedCompanyId) &&
      Number.isFinite(normalizedTicketId) &&
      normalizedCompanyId > 0 &&
      normalizedTicketId > 0
  );

  const loadHistoryPage = useCallback(
    async (page, replaceExisting) => {
      const response = await fetchTicketChatMessages({
        supportTicketId: normalizedTicketId,
        companyId: normalizedCompanyId,
        page,
        limit: DEFAULT_PAGE_SIZE,
      });

      const normalizedRows = (response.rows || [])
        .map((item) => {
          const normalized = normalizeMessage(item);
          return {
            ...normalized,
            isOwn: isOwnMessage(normalized, currentUser),
          };
        })
        .filter((item) => item.message.length > 0);

      setMessages((prev) =>
        replaceExisting
          ? sortMessages(normalizedRows)
          : mergeDedupedMessages(prev, normalizedRows)
      );

      const canLoadMoreByCount =
        Number(response.count) > (page + 1) * Number(response.limit || DEFAULT_PAGE_SIZE);
      const canLoadMoreByPageSize =
        normalizedRows.length === Number(response.limit || DEFAULT_PAGE_SIZE);
      setHasMore(canLoadMoreByCount || canLoadMoreByPageSize);
    },
    [currentUser, normalizedCompanyId, normalizedTicketId]
  );

  useEffect(() => {
    let alive = true;

    if (!isContextValid) {
      setHistoryError("Invalid ticket or company context for chat.");
      setConnectionState("disconnected");
      return () => {};
    }

    const initialize = async () => {
      setHistoryError("");
      setSocketError("");
      setLoadingHistory(true);
      setConnectionState("disconnected");
      setMessages([]);
      setNextPage(0);

      try {
        await loadHistoryPage(0, true);
        if (!alive) return;
        setNextPage(1);
      } catch (error) {
        if (!alive) return;
        setHistoryError(error.message || "Unable to load chat history.");
      } finally {
        if (alive) setLoadingHistory(false);
      }

      if (!alive) return;

      const chatSocket = createTicketChatSocket({
        token,
        onConnect: () => {
          if (!alive) return;
          setConnectionState("connected");
          setSocketError("");
        },
        onDisconnect: () => {
          if (!alive) return;
          setConnectionState("disconnected");
        },
        onReconnectAttempt: () => {
          if (!alive) return;
          setConnectionState("reconnecting");
        },
        onReconnect: () => {
          if (!alive) return;
          setConnectionState("connected");
        },
        onJoined: () => {
          if (!alive) return;
          setSocketError("");
        },
        onMessage: (payload) => {
          if (!alive) return;

          const serverMessage = normalizeMessage(payload);
          if (!serverMessage.message) return;
          serverMessage.isOwn = isOwnMessage(serverMessage, currentUser);

          setMessages((prev) => {
            const existingIndexById = prev.findIndex(
              (item) =>
                item.id !== null &&
                item.id !== undefined &&
                serverMessage.id !== null &&
                serverMessage.id !== undefined &&
                String(item.id) === String(serverMessage.id)
            );

            if (existingIndexById >= 0) {
              const next = [...prev];
              next[existingIndexById] = {
                ...next[existingIndexById],
                ...serverMessage,
                pending: false,
              };
              return sortMessages(next);
            }

            const pendingIndex = prev.findIndex(
              (item) =>
                item.pending &&
                item.message.trim() === serverMessage.message.trim() &&
                Math.abs(toTimeValue(item.createdAt) - toTimeValue(serverMessage.createdAt)) <
                  2 * 60 * 1000
            );

            const next =
              pendingIndex >= 0
                ? prev.filter((_, idx) => idx !== pendingIndex)
                : [...prev];
            next.push({ ...serverMessage, pending: false });
            return sortMessages(next);
          });
        },
        onTicketError: (payload) => {
          if (!alive) return;
          setSocketError(payload?.message || "Ticket chat error.");
        },
      });

      socketRef.current = chatSocket;
      setConnectionState("connecting");
      chatSocket.connect();
      chatSocket.joinRoom(roomPayload);
    };

    initialize();

    return () => {
      alive = false;
      if (socketRef.current) {
        socketRef.current.leaveRoom(roomPayload);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [currentUser, isContextValid, loadHistoryPage, roomPayload, token]);

  const handleLoadOlder = async () => {
    if (loadingOlder || loadingHistory || !hasMore || !isContextValid) return;
    setLoadingOlder(true);
    setHistoryError("");
    try {
      await loadHistoryPage(nextPage, false);
      setNextPage((prev) => prev + 1);
    } catch (error) {
      setHistoryError(error.message || "Unable to load older messages.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSend = async (text) => {
    if (!isContextValid) {
      setSocketError("Missing ticket/company context.");
      return false;
    }

    if (!socketRef.current || !socketRef.current.isConnected()) {
      setSocketError("Reconnect required before sending a message.");
      return false;
    }

    const cleanedText = String(text || "").trim();
    if (!cleanedText) {
      setSocketError("Message is required.");
      return false;
    }
    if (cleanedText.length > MAX_MESSAGE_LENGTH) {
      setSocketError("Message exceeds maximum length (2000 characters).");
      return false;
    }

    const clientTempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMessage = normalizeMessage({
      clientTempId,
      message: cleanedText,
      messageType: "text",
      createdAt: new Date().toISOString(),
      senderName: currentUser?.name || currentUser?.username || "You",
      senderRole: currentUser?.role || "admin",
      senderId: currentUser?.id ?? currentUser?.userId ?? null,
      isOwn: true,
      pending: true,
    });

    setMessages((prev) => sortMessages([...prev, optimisticMessage]));
    setSocketError("");

    socketRef.current.sendMessage({
      companyId: normalizedCompanyId,
      supportTicketId: normalizedTicketId,
      message: cleanedText,
      messageType: "text",
    });

    return true;
  };

  const connected = connectionState === "connected";
  const connectionLabel = connectionLabelByState[connectionState] || "Disconnected";

  return (
    <section className={`ticket-card ticket-chat-card ${hideHeader ? "embedded" : ""} ${className}`}>
      {!hideHeader ? (
        <div className="ticket-card-header ticket-chat-header">
          <div>
            <h3>Ticket Chat</h3>
            <span>
              Ticket #{normalizedTicketId} | Company #{normalizedCompanyId}
            </span>
          </div>
          <span className={`ticket-chat-status ${connectionState}`}>{connectionLabel}</span>
        </div>
      ) : (
        <div className="ticket-chat-embedded-status">
          <span className={`ticket-chat-status ${connectionState}`}>{connectionLabel}</span>
        </div>
      )}

      {historyError ? <div className="ticket-chat-error">{historyError}</div> : null}
      {socketError ? <div className="ticket-chat-error">{socketError}</div> : null}
      {!token ? (
        <div className="ticket-chat-error">
          Missing auth token. Please log in again to access chat.
        </div>
      ) : null}
      {!isContextValid ? (
        <div className="ticket-chat-error">
          Invalid ticket or company ID. Chat connection is unavailable.
        </div>
      ) : null}

      <MessageList
        messages={messages}
        loading={loadingHistory}
        loadingOlder={loadingOlder}
        hasMore={hasMore}
        onLoadOlder={handleLoadOlder}
      />

      <MessageComposer
        disabled={!connected || !isContextValid}
        onSend={handleSend}
        placeholder={
          connected
            ? "Write a message and press Enter to send"
            : "Chat is offline. Reconnect to send."
        }
      />
    </section>
  );
};

export default TicketChatPanel;
